'use client';

import { useEffect, useRef, useState } from 'react';
import { exploreVideoScheduler } from '@/lib/explore-video-scheduler';

/** Only an admitted, visible card mounts a player; original media is never a fallback. */
export function useViewportPreviewVideo(source: string | undefined, enabled: boolean) {
  const containerRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [admittedSource, setAdmittedSource] = useState<string>();
  const [blockedSource, setBlockedSource] = useState<string>();
  const shouldLoadVideo = Boolean(source && enabled && admittedSource === source && blockedSource !== source);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !source || !enabled || blockedSource === source) return;
    return exploreVideoScheduler.observe(container, active => {
      if (!active) {
        // Release immediately, before React commits another card's admission.
        const video = videoRef.current;
        if (video) { video.pause(); video.removeAttribute('src'); video.load(); }
      }
      setAdmittedSource(active ? source : undefined);
    });
  }, [source, enabled, blockedSource]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoadVideo) return;
    let cancelled = false;
    const block = () => { if (!cancelled) setBlockedSource(source); };
    const timeout = window.setTimeout(block, 10_000);
    const playing = () => window.clearTimeout(timeout);
    video.addEventListener('playing', playing);
    video.muted = true;
    try {
      void video.play().then(() => { if (cancelled) video.pause(); }).catch(error => {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        block();
      });
    } catch { block(); }
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      video.removeEventListener('playing', playing);
      video.pause(); video.removeAttribute('src'); video.load();
    };
  }, [shouldLoadVideo, source]);

  return { containerRef, videoRef, shouldLoadVideo };
}
